import { z } from 'zod';
import { asId } from '@shared/brand';
import type { Birthday } from '@domain/entities/Birthday';
import { CalendarDate } from '@domain/value-objects/CalendarDate';
import { arrayOr, nullableText, textOr } from './base';

/**
 * Las filas de un mes de cumpleaños, tal como las guarda el servidor en
 * `birthdayMonths/{01…12}`, o como las arma el demo.
 *
 * Tolerante por fila, como el resto de `codecs/`: una fila ilegible se salta y
 * las demás se celebran. Un día que no existe en su mes —un 31 de abril tecleado
 * a mano en la hoja de RR.HH.— cuenta como ilegible: no hay día en que pintarlo.
 */

/** El año bisiesto de referencia: el 29 de febrero es un cumpleaños válido. */
const LEAP_YEAR = 2000;

/** Sólo fotos servidas por https; cualquier otra cosa se queda en iniciales. */
const safePhoto = (url: string | null): string | null =>
  url !== null && url.startsWith('https://') ? url : null;

const firstWord = (name: string): string => name.split(/\s+/)[0] ?? name;

const rowSchema = z
  .object({
    personId: z.string().min(1),
    displayName: z.string().trim().min(1),
    firstName: textOr(''),
    areaId: textOr(''),
    areaName: textOr(''),
    photoURL: nullableText(),
    month: z.number().int().min(1).max(12),
    day: z.number().int().min(1),
  })
  .transform((raw): Birthday | null => {
    if (raw.day > CalendarDate.daysInMonth(LEAP_YEAR, raw.month)) return null;
    const firstName = raw.firstName.trim();
    return {
      personId: asId<'PersonId'>(raw.personId),
      displayName: raw.displayName,
      firstName: firstName.length > 0 ? firstName : firstWord(raw.displayName),
      areaId: asId<'AreaId'>(raw.areaId),
      areaName: raw.areaName.trim(),
      photoURL: safePhoto(raw.photoURL),
      month: raw.month,
      day: raw.day,
    };
  });

const listSchema = arrayOr(rowSchema);

export const decodeBirthdays = (data: unknown): Birthday[] => {
  const parsed = listSchema.safeParse(data);
  if (!parsed.success) return [];
  return parsed.data.filter((row): row is Birthday => row !== null);
};
